"use strict";
/**
 * A small package to directly interact with the World-Wide-Lab API.
 *
 * @remarks
 * If you use one of the libraries with a supported integration package,
 * you may not need this package.
 *
 * @packageDocumentation
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VERSION = exports.Session = exports.Participant = exports._ClientModel = exports.Client = void 0;
const version_1 = require("./version");
Object.defineProperty(exports, "VERSION", { enumerable: true, get: function () { return version_1.VERSION; } });
const PARTICIPANT_ID_KEY = "WWL_PARTICIPANT_ID";
/**
 * You will need to create an instance of this class to communicate with the
 * World-Wide-Lab server. You can then use the methods of this class to create
 * participants, sessions and responses.
 *
 * @public
 */
class Client {
    /**
     * Create a new Client instance
     * @param options - Options to create the client with
     */
    constructor(
    /**
     * Options the Client was created with
     */
    options) {
        this.options = options;
        console.log("Initializing Client", options);
        this._library = "@world-wide-lab/client";
    }
    /**
     * Call an endpoint at the API server
     * @param method - Which HTTP method to use: GET, POST or PUT
     * @param endpoint - The endpoint to call, e.g. /participant/
     * @param data - The data to send to the server (optional)
     * @param options - Additional options to use
     * @returns The JSON body of the response from the server
     */
    async call(method, endpoint, data, options) {
        const slash = endpoint.startsWith("/") ? "" : "/";
        const url = new URL(`v1${slash}${endpoint}`, this.options.url).toString();
        const body = data ? JSON.stringify(data) : undefined;
        const fetchOptions = {
            method,
            headers: {
                "Content-Type": data ? "application/json" : "none",
            },
            body,
            ...options,
        };
        const response = await fetch(url, fetchOptions);
        return response.json();
    }
    /**
     * Create a new participant  from sractch. Use getParticipant() if you want to get an existing participant
     * or store their id. This function will not keep track of a participant's id.
     * @returns A new Participant instance
     * @see getParticipant
     */
    async createParticipant(participantParams = undefined) {
        const result = await this.call("POST", "/participant/", participantParams);
        return new Participant(this, result.participantId);
    }
    /**
     * Start a new Session. If a participant's id is stored, it will be used. see getParticipant().
     * @param sessionOptions - Options to create the session with
     * @returns A new Session instance
     */
    async createSession(sessionOptions) {
        const sessionData = {
            studyId: sessionOptions.studyId,
            clientMetadata: {
                version: version_1.VERSION,
                library: this._library,
            },
        };
        // Generate Client Metadata
        if (this._libraryVersion) {
            sessionData.clientMetadata.libraryVersion = this._libraryVersion;
        }
        if (typeof window !== "undefined" && "location" in window) {
            sessionData.clientMetadata.url = window.location.href;
            const search = window.location.search;
            if (search.length > 0) {
                sessionData.clientMetadata.searchParams = {};
                const searchParams = new URLSearchParams(search);
                for (const [key, value] of searchParams) {
                    sessionData.clientMetadata.searchParams[key] = value;
                }
            }
        }
        if (typeof navigator !== "undefined") {
            sessionData.clientMetadata.navigator = {
                language: navigator.language,
                languages: navigator.languages,
            };
        }
        // Create a participant if requested
        let participant;
        if (sessionOptions.participant && sessionOptions.linkParticipant) {
            console.warn("Both participant and linkParticipant are set. Ignoring linkParticipant.");
        }
        if (sessionOptions.participant) {
            participant = sessionOptions.participant;
        }
        else if (sessionOptions.linkParticipant) {
            participant = await this.getParticipant();
        }
        if (participant) {
            sessionData.participantId = participant.participantId;
        }
        if (sessionOptions.privateInfo) {
            sessionData.privateInfo = sessionOptions.privateInfo;
        }
        if (sessionOptions.publicInfo) {
            sessionData.publicInfo = sessionOptions.publicInfo;
        }
        const result = await this.call("POST", "/session/", sessionData);
        const session = new Session(this, result.sessionId);
        // Link participant
        if (participant) {
            session.participant = participant;
        }
        return session;
    }
    /**
     * Create a new Response. See also {@link Session.response}
     * @param opts - Options to create the response with
     * @returns true if the response was created successfully
     */
    async createResponse(opts) {
        const result = await this.call("POST", "/response/", opts);
        return true;
    }
    /**
     * Store the participantId of the last person that participated using your website.
     * @param participantId - The participantId to store
     * @returns true if the id was stored successfully
     */
    storeParticipantId(participantId) {
        if (!window.localStorage) {
            console.warn("localStorage API is not available. Participant-information will not be stored.");
            return false;
        }
        window.localStorage.setItem(PARTICIPANT_ID_KEY, participantId);
        return true;
    }
    /**
     * Get the participantId of the last person that participated using your website (if their id
     * was stored).
     * @returns The participantId or undefined if no id was stored
     */
    getStoredParticipantId() {
        if (!window.localStorage) {
            console.warn("localStorage API is not available. Participant-information will not be stored.");
            return undefined;
        }
        return window.localStorage.getItem(PARTICIPANT_ID_KEY) || undefined;
    }
    /**
     * Delete the stored participantId.
     */
    deleteStoredParticipantId() {
        window.localStorage.removeItem(PARTICIPANT_ID_KEY);
    }
    /**
     * Get a Participant. If someone has already participanted on this machine and their IDs is
     * saved, will return this existing participant. Otherwise, will create a new participant and
     * store the corresponding id.
     * @returns A Participant instance
     */
    async getParticipant() {
        const id = this.getStoredParticipantId();
        if (id !== undefined) {
            return new Participant(this, id);
        }
        const participant = await this.createParticipant();
        return participant;
    }
}
exports.Client = Client;
/**
 * The Base class for all data model classes, such as {@link Participant} and {@link Session}.
 *
 * @remarks
 *
 * This class only contains a link to the client instance that created the
 * model i.e. if you create a new Participant instance, it will have an
 * internal link to the client that created it.
 *
 * This class should not be used directly.
 *
 * @internal
 */
class _ClientModel {
    constructor(clientInstance) {
        this.clientInstance = clientInstance;
    }
}
exports._ClientModel = _ClientModel;
/**
 * A World-Wide-Lab participant, typically used to link multiple {@link Session}s.
 * @public
 */
class Participant extends _ClientModel {
    constructor(clientInstance, participantId) {
        super(clientInstance);
        this.participantId = participantId;
    }
    /**
     * Update the participant's meta-data.
     * @param data - The data to update. Can contain privateInfo and/or publicInfo.
     *   The publicInfo can be retrieved later on without authentication, the
     *   privateInfo can only be downlaoded later on by the researcher.
     * @returns true if the update was successful
     */
    async update(data) {
        const result = await this.clientInstance.call("PUT", `/participant/${this.participantId}`, data);
        return result.success;
    }
    /**
     * Retrieve public meta-data for a participant
     * @returns The participant's publicInfo meta data
     */
    getPublicInfo() {
        return this.clientInstance.call("GET", `/participant/${this.participantId}`);
    }
    /**
     * Store a participant's participantId, so it can later be re-used via
     * {@link Client.getParticipant}.
     * @returns true if the participantId was stored successfully
     */
    storeParticipantId() {
        return this.clientInstance.storeParticipantId(this.participantId);
    }
}
exports.Participant = Participant;
/**
 * A world-wide-lab session, corresponding to a person's participation in a
 * study. Use this class to capture responses.
 *
 * @public
 */
class Session extends _ClientModel {
    constructor(clientInstance, sessionId) {
        super(clientInstance);
        this.sessionId = sessionId;
    }
    /**
     * Create a new Response.
     */
    response(opts) {
        const createResponseOptions = { sessionId: this.sessionId, ...opts };
        return this.clientInstance.createResponse(createResponseOptions);
    }
    /**
     * Finish the session. This will mark the session as finished.
     * @returns true if the session was finished successfully
     */
    async finish() {
        const result = await this.clientInstance.call("POST", "/session/finish", {
            sessionId: this.sessionId,
        });
        return result.success;
    }
    /**
     * Update the session's meta-data.
     * @param data - The data to update. Can contain privateInfo and/or publicInfo.
     *   The publicInfo can be retrieved later on without authentication, the
     *   privateInfo can only be downlaoded later on by the researcher.
     */
    async update(data) {
        const result = await this.clientInstance.call("PUT", `/session/${this.sessionId}`, data);
        return result.success;
    }
    /**
     * Retrieve public meta-data for a session
     * @returns The session's publicInfo meta data
     */
    getPublicInfo() {
        return this.clientInstance.call("GET", `/session/${this.sessionId}`);
    }
    /**
     * Store the participantId of the participant that is doing this session.
     * @returns true if the participantId was stored successfully
     */
    storeParticipantId() {
        if (this.participant) {
            return this.participant.storeParticipantId();
        }
        console.error("Cannot store participantId: No participant set / created. Do you maybe want to set linkParticipant to true?");
        return false;
    }
}
exports.Session = Session;
//# sourceMappingURL=index.js.map